import { useEffect, useState } from "react";
import { Eraser, Plus } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  ChatColumn,
  type PanelMessage,
  type TextPanelState,
} from "@/features/text/ChatColumn";
import { PromptBar } from "@/features/shared/PromptBar";
import { SectionRail } from "@/features/shared/SectionRail";
import { apiJson, apiStream } from "@/lib/api";
import {
  MAX_TEXT_PANELS,
  pickDefaultTextModels,
  type ModelInfo,
  type ModelsResponse,
} from "@/lib/playground";

const DEFAULT_PANEL_COUNT = 4;

const newPanel = (model: string): TextPanelState => ({
  id: crypto.randomUUID(),
  model,
  messages: [],
  streaming: false,
  error: null,
  stats: null,
});

export const TextArenaPage = () => {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [panels, setPanels] = useState<TextPanelState[]>([]);

  useEffect(() => {
    apiJson<ModelsResponse>("/api/models")
      .then((response) => {
        setModels(response.models);
        setPanels((current) =>
          current.length
            ? current
            : pickDefaultTextModels(response.models, DEFAULT_PANEL_COUNT).map(
                newPanel
              )
        );
      })
      .catch(() => setModels([]));
  }, []);

  const updatePanel = (
    id: string,
    update: (panel: TextPanelState) => TextPanelState
  ) => {
    setPanels((current) =>
      current.map((panel) => (panel.id === id ? update(panel) : panel))
    );
  };

  const appendAssistantDelta = (id: string, content: string) => {
    updatePanel(id, (panel) => {
      const last = panel.messages.at(-1);
      if (last?.role === "assistant") {
        return {
          ...panel,
          messages: [
            ...panel.messages.slice(0, -1),
            { role: "assistant", content: last.content + content },
          ],
        };
      }
      return {
        ...panel,
        messages: [...panel.messages, { role: "assistant", content }],
      };
    });
  };

  const runPanel = async (
    id: string,
    model: string,
    history: PanelMessage[]
  ) => {
    const startedAt = performance.now();

    try {
      await apiStream(
        "/api/chat/stream",
        {
          method: "POST",
          body: JSON.stringify({ model, messages: history }),
        },
        (event) => {
          if (event.type === "delta") appendAssistantDelta(id, event.content);
          if (event.type === "error") {
            updatePanel(id, (panel) => ({ ...panel, error: event.message }));
          }
          if (event.type === "done") {
            updatePanel(id, (panel) => ({
              ...panel,
              stats: {
                durationMs: performance.now() - startedAt,
                totalTokens: event.usage?.total_tokens ?? null,
              },
            }));
          }
        }
      );
    } catch (error) {
      updatePanel(id, (panel) => ({
        ...panel,
        error: error instanceof Error ? error.message : "Request failed.",
      }));
    } finally {
      updatePanel(id, (panel) => ({ ...panel, streaming: false }));
    }
  };

  const send = (prompt: string) => {
    const targets = panels.map((panel) => ({
      id: panel.id,
      model: panel.model,
      history: [
        ...panel.messages,
        { role: "user", content: prompt } as PanelMessage,
      ],
    }));

    setPanels((current) =>
      current.map((panel) => ({
        ...panel,
        messages: [...panel.messages, { role: "user", content: prompt }],
        streaming: true,
        error: null,
      }))
    );

    for (const target of targets) {
      void runPanel(target.id, target.model, target.history);
    }
  };

  const addPanel = () => {
    if (panels.length >= MAX_TEXT_PANELS) return;
    const used = new Set(panels.map((panel) => panel.model));
    const nextModel =
      models.find((model) => !used.has(model.id))?.id ??
      models[0]?.id ??
      "anthropic/claude-sonnet-4.6";
    setPanels((current) => [...current, newPanel(nextModel)]);
  };

  const clearAll = () => {
    setPanels((current) =>
      current.map((panel) => ({
        ...panel,
        messages: [],
        error: null,
        stats: null,
      }))
    );
  };

  const busy = panels.some((panel) => panel.streaming);

  return (
    <div className="space-y-4">
      <SectionRail
        index="[01]"
        kicker="Text arena"
        meta={`${models.length} models · max ${MAX_TEXT_PANELS} panels`}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          One prompt, up to {MAX_TEXT_PANELS} models side by side. Each column
          keeps its own conversation history.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={clearAll} disabled={busy}>
            <Eraser className="h-4 w-4" />
            Clear chats
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={addPanel}
            disabled={busy || panels.length >= MAX_TEXT_PANELS}
          >
            <Plus className="h-4 w-4" />
            Add model ({panels.length}/{MAX_TEXT_PANELS})
          </Button>
        </div>
      </div>

      <PromptBar
        onSend={send}
        busy={busy}
        placeholder="Ask all models the same thing… (Enter to send, Shift+Enter for a new line)"
        sendLabel="Send to all"
      />

      <div className="grid border-l border-t md:grid-cols-2 xl:grid-cols-3">
        {panels.map((panel) => (
          <ChatColumn
            key={panel.id}
            panel={panel}
            models={models}
            onModelChange={(model) =>
              updatePanel(panel.id, (current) => ({ ...current, model }))
            }
            onRemove={() =>
              setPanels((current) =>
                current.filter((candidate) => candidate.id !== panel.id)
              )
            }
            canRemove={panels.length > 1}
          />
        ))}
      </div>
    </div>
  );
};
